import type { ComponentProps } from 'react';
import React, { useMemo } from 'react';
import type { ImageSourcePropType, ViewProps } from 'react-native';
import { Pressable, ScrollView, StyleSheet, View } from 'react-native';
import type { IconName } from '@sweetgreen/garnish';
import {
  BodyText,
  Button,
  DisplayText,
  formatGoogleAddress,
  HStack,
  Icon,
  Illus404,
  IllusDelivery_1,
  Image,
  LoadingPlaceholder,
  switchcase,
  theme,
  useBreakpointHelpers,
  usePressableState,
  useResponsive,
  VStack,
} from '@sweetgreen/garnish';

import { AddressSearchResults } from '@order/components';
import type { Address } from '@order/graphql';
import { LocationStatus } from '@order/graphql';
import { useFeatureFlag } from '@order/LaunchDarkly';
import { useLocalizationContext } from '@order/Localization';

import { useReorderState } from '../../../ReorderingScreen/hooks';
import type { LocationResultsProps } from '../../LocationScreen.types';
import { LocationResultsV2 } from '../LocationResultsV2';

export const DeliveryLocationLayout = (props: LocationResultsProps) => {
  const {
    customerAddresses,
    foundLocation,
    noLocationFound,
    predictionsData,
    loading,
    fetchingDelivery,
    isAddingAddress,
    onPredictionPress,
    searchString,
    predictionsSearchString,
    googlePlacesAddress,
    isShownLocationWarningModal,
    onOrderNow,
  } = props;

  const { currentBreakpoint } = useResponsive();
  const { t } = useLocalizationContext();

  // ─────────────────────────────────────────────────────────────────

  const hasSelectedDeliveryLocation =
    foundLocation?.restaurant?.id !== undefined;

  const shouldShowAutocompleteResults =
    searchString !== predictionsSearchString;

  // NOTE: We temporarily utilize those conditions temporarily
  //       because we have two versions of location results components.

  const shouldUseLocationsResultsRedesign =
    useFeatureFlag('XP-3156-short-locations-results-redesign') &&
    currentBreakpoint.isMobile;
  const isLoadingLocation = loading && fetchingDelivery;
  const shouldShowLoading =
    !shouldUseLocationsResultsRedesign && isLoadingLocation;
  const shouldShowSelectedDeliveryLocationsDetails =
    hasSelectedDeliveryLocation && !shouldShowAutocompleteResults;
  const shouldShowNoLocationFoundView =
    noLocationFound && !shouldShowAutocompleteResults;
  const shouldShowSavedAddresses = !predictionsSearchString;
  const shouldRenderLocationResultsRedesignedView =
    shouldUseLocationsResultsRedesign &&
    (shouldShowSelectedDeliveryLocationsDetails || isLoadingLocation);

  // ─────────────────────────────────────────────────────────────────

  /**
   * Returns a locations array with a selected delivery location for `LocationResultsV2` component.
   */
  const deliveryLocation = useMemo<
    ComponentProps<typeof LocationResultsV2>['locations']
  >(() => {
    if (!foundLocation || foundLocation.__typename !== 'Location') return [];

    const { restaurant, estimateRange } = foundLocation;
    const { flexMessage, phone, slug } = restaurant ?? {};

    const id = googlePlacesAddress?.googlePlaceId ?? '';
    const address = formatGoogleAddress(googlePlacesAddress);
    const isClosed = foundLocation.status !== 'OPEN';

    const {
      start: estimatedDeliveryRangeStart,
      end: estimatedDeliveryRangeEnd,
    } = estimateRange;
    const estimatedDeliveryTime =
      estimatedDeliveryRangeStart === estimatedDeliveryRangeEnd
        ? `${estimatedDeliveryRangeStart}`
        : `${estimatedDeliveryRangeStart}-${estimatedDeliveryRangeEnd}`;

    const existingAddress = customerAddresses?.find(
      ({ googlePlaceId }) => id === googlePlaceId,
    );
    const name = existingAddress?.name ?? t('general.delivery');

    return [
      {
        id,
        name,
        isClosed,
        address,
        estimatedDeliveryTime,
        flexMessage,
        phone,
        restaurantSlug: slug,
      },
    ];
  }, [customerAddresses, foundLocation, googlePlacesAddress, t]);

  const { reordering: isReordering, restaurantSlug: reorderRestaurantSlug } =
    useReorderState();
  const activeReorderRestaurantSlug = isReordering
    ? reorderRestaurantSlug
    : undefined;

  // ─────────────────────────────────────────────────────────────────────

  if (shouldRenderLocationResultsRedesignedView)
    return (
      <LocationResultsV2
        locations={deliveryLocation}
        activePinLocationId={googlePlacesAddress?.googlePlaceId}
        locationSearchType="delivery"
        isLoading={(loading && fetchingDelivery) || isAddingAddress}
        activeReorderRestaurantSlug={activeReorderRestaurantSlug}
        shouldHideDetailsModal={isShownLocationWarningModal}
        onCardPress={onOrderNow}
      />
    );

  if (shouldShowLoading) return <LoadingFeedback />;

  if (shouldShowSelectedDeliveryLocationsDetails)
    return <DeliveryLocationFound {...props} />;

  if (shouldShowNoLocationFoundView)
    return <DeliveryLocationNotFound {...props} />;

  if (shouldShowSavedAddresses) return <PreviousAddresses {...props} />;

  // ─────────────────────────────────────────────────────────────────

  return (
    <AddressSearchResults
      skipLoading
      customerAddresses={customerAddresses}
      predictionsData={predictionsData}
      onPress={onPredictionPress}
    />
  );
};

export const DeliveryLocationNotFound = ({
  googlePlacesAddress,
  noLocationFound,
  onResetDeliverySearch,
  onOrderPickup,
}: LocationResultsProps) => {
  const { t } = useLocalizationContext();
  const { currentBreakpoint, match } = useResponsive();
  const { isMobile } = currentBreakpoint;
  const address = formatGoogleAddress(googlePlacesAddress);
  const onTryNewAddress = React.useCallback(() => {
    onResetDeliverySearch(true);
  }, [onResetDeliverySearch]);

  const ctaStackStyles = [
    styles.ctaStack,
    match([styles.ctaStackXS, styles.ctaStackSM]),
  ];
  const ctaSize = match(['medium', 'large']) as ComponentProps<
    typeof Button
  >['size'];

  if (!noLocationFound) return null;

  return (
    <ContentWrapper testID="location.delivery.no-location-found">
      <DesktopImage source={noLocationFoundIllustration} />

      {isMobile ? (
        <>
          <DisplayText style={[styles.mobileText, styles.smallSpacer]} size={4}>
            {t('delivery.unavailable-mobile')}
          </DisplayText>
          <BodyText style={[styles.mobileText, styles.smallSpacer]} size={4}>
            {address}
          </BodyText>
        </>
      ) : (
        <>
          <BodyText bold style={[styles.text, styles.addressMargin]} size={3}>
            {address}
          </BodyText>
          <BodyText style={[styles.text, styles.spacer]} size={3}>
            {t('delivery.unavailable')}
          </BodyText>
        </>
      )}

      <HStack
        style={ctaStackStyles}
        itemsPerRow={2}
        shouldFillAvailableSpace={false}
        customSpacing={theme.spacing.medium}
      >
        <Button
          palette="secondary"
          onPress={onTryNewAddress}
          size={ctaSize}
          style={styles.cta}
        >
          {isMobile ? t('general.close') : t('delivery.new-address')}
        </Button>

        <Button onPress={onOrderPickup} size={ctaSize} style={styles.cta}>
          {t('delivery.order-pickup')}
        </Button>
      </HStack>
    </ContentWrapper>
  );
};

export const DeliveryLocationFound = ({
  googlePlacesAddress,
  foundLocation,
  isAddingAddress,
  customerAddresses,
  onOrderNow,
}: LocationResultsProps) => {
  const { t } = useLocalizationContext();
  const { currentBreakpoint: breakpoint } = useBreakpointHelpers();
  const { reordering } = useReorderState();
  const address = formatGoogleAddress(googlePlacesAddress);
  const start = foundLocation?.estimateRange?.start;
  const end = foundLocation?.estimateRange?.end;
  const isClosed = foundLocation?.status === LocationStatus.Closed;
  const estimate = start === end ? start : `${start}-${end}`;
  const spacing = breakpoint.isMobile ? styles.smallSpacer : styles.spacer;
  const openStyle = styles.smallerSpacer;
  const closedStyle = breakpoint.isMobile ? styles.smallSpacer : styles.spacer;
  const addressStyle = isClosed ? closedStyle : openStyle;
  const hintStyle = breakpoint.isMobile && styles.smallSpacer;
  const callToAction = t(`delivery.order-${isClosed ? 'later' : 'now'}`);
  const placeId = googlePlacesAddress?.googlePlaceId;
  const existingAddress = customerAddresses?.find(
    ({ googlePlaceId }) => placeId === googlePlaceId,
  );
  const addressName = existingAddress?.name;

  return (
    <ContentWrapper testID="location.delivery.location-found">
      <DesktopImage source={foundLocationIllustration} />
      {addressName ? (
        <BodyText bold style={styles.text} size={3}>
          {addressName}
        </BodyText>
      ) : null}
      <BodyText style={[styles.text, addressStyle]} size={3}>
        {address}
      </BodyText>
      {!isClosed && (
        <BodyText style={[styles.text, spacing]} size={4} bold>
          {t('delivery.estimate', { estimate })}
        </BodyText>
      )}
      {isClosed ? (
        <BodyText style={[styles.text, styles.hint, hintStyle]} size={4}>
          {t('delivery.order-later-hint')}
        </BodyText>
      ) : null}
      <Button
        width="100%"
        size="large"
        testID="location.delivery.order-now"
        style={styles.text}
        isLoading={Boolean(reordering) || isAddingAddress}
        onPress={onOrderNow}
      >
        {callToAction}
      </Button>
    </ContentWrapper>
  );
};

const PreviousAddresses = ({
  customerAddresses,
  onDeliveryPress,
  setSearchString,
}: LocationResultsProps) => {
  const { currentBreakpoint } = useBreakpointHelpers();
  const { isMobile } = currentBreakpoint;

  if (!customerAddresses || customerAddresses.length === 0) {
    if (isMobile) return null;

    return (
      <ContentWrapper testID="location.delivery.empty-state">
        <DesktopImage source={foundLocationIllustration} />
      </ContentWrapper>
    );
  }

  return (
    <View style={styles.previousAddress}>
      <ScrollView contentContainerStyle={styles.grow}>
        <VStack
          divider
          customSpacing={theme.spacing.large + theme.spacing.small}
          style={isMobile ? styles.addressesMobile : styles.addressesDesktop}
          testID="location.previous-addresses"
        >
          {customerAddresses.map((address) => (
            <PreviousAddress
              key={address.id}
              address={address}
              onPress={() => {
                setSearchString(formatGoogleAddress(address) ?? '');
                onDeliveryPress(address);
              }}
            />
          ))}
        </VStack>
      </ScrollView>
    </View>
  );
};

const PreviousAddress = ({
  address,
  onPress,
}: {
  readonly address: Address;
  readonly onPress: () => void;
}) => {
  const { t } = useLocalizationContext();
  const ref = React.useRef(null);
  const { isInteracting } = usePressableState(ref);

  return (
    <Pressable
      ref={ref}
      style={styles.address}
      key={address.id}
      testID={`account.addresses.${address.name}-card-pressable`}
      accessibilityRole="button"
      accessibilityHint={t('account.addresses.address-hint')}
      accessibilityLabel={t('account.addresses.address-label', {
        address: address?.name,
      })}
      onPress={onPress}
    >
      <View style={styles.icon}>
        <Icon
          width={24}
          height={24}
          name={switchcase<IconName>({
            home: 'IconHomeStrokeSm',
            work: 'IconWorkStrokeSm',
            custom: 'IconCustomStrokeSm',
          })('IconCustomStrokeSm')(address.name ?? '')}
        />
      </View>
      <BodyText underline={isInteracting} numberOfLines={1}>
        {formatGoogleAddress(address, true)}
      </BodyText>
    </Pressable>
  );
};

const LoadingFeedback = () => {
  const { currentBreakpoint } = useBreakpointHelpers();

  return (
    <ContentWrapper testID="location.delivery.loading-feedback">
      {!currentBreakpoint.isMobile && (
        <View style={styles.placeholder}>
          <LoadingPlaceholder
            borderRadius={theme.radius.medium}
            rowHeight={241.38}
            palette="quinoa"
          />
        </View>
      )}
      <View style={styles.placeholder}>
        <LoadingPlaceholder
          borderRadius={theme.radius.medium}
          rowHeight={24}
          palette="quinoa"
        />
      </View>
      {currentBreakpoint.isMobile ? (
        <View style={styles.placeholderSmall}>
          <LoadingPlaceholder
            borderRadius={theme.radius.medium}
            rowHeight={16}
            palette="quinoa"
          />
        </View>
      ) : null}
      <View style={styles.placeholder}>
        <LoadingPlaceholder
          borderRadius={theme.radius.xlarge}
          rowHeight={48}
          palette="quinoa"
        />
      </View>
    </ContentWrapper>
  );
};

const DesktopImage = ({ source }: { readonly source: ImageSourcePropType }) => {
  const { currentBreakpoint } = useBreakpointHelpers();

  if (currentBreakpoint.isMobile) return null;

  return (
    <View style={styles.illustrationWrapper}>
      <Image
        style={styles.illustration}
        source={source}
        accessibilityIgnoresInvertColors
      />
    </View>
  );
};

const ContentWrapper = ({ testID, children }: ViewProps) => {
  const { isMobile } = useBreakpointHelpers();

  return (
    <View
      testID={testID}
      style={[styles.content, isMobile && styles.contentBorder]}
    >
      {children}
    </View>
  );
};

const foundLocationIllustration = IllusDelivery_1;
const noLocationFoundIllustration = Illus404;

const styles = StyleSheet.create({
  grow: {
    flexGrow: 1,
  },
  content: {
    paddingVertical: theme.spacing.large,
    width: '100%',
    alignItems: 'center',
    justifyContent: 'center',
  },
  ctaStack: {
    width: '100%',
    paddingHorizontal: theme.spacing.medium,
  },
  ctaStackXS: {
    paddingHorizontal: theme.spacing.small,
  },
  ctaStackSM: {
    paddingHorizontal: theme.spacing.medium,
  },
  cta: {
    width: '100%',
    paddingHorizontal: theme.spacing.xsmall,
  },
  contentBorder: {
    borderTopColor: theme.colors.BLACK,
    borderTopWidth: 1,
    borderStyle: 'solid',
  },
  previousAddress: {
    height: '100%',
    backgroundColor: theme.colors.OATMEAL,
    zIndex: theme.zIndex.fixed + 1, // cover up components that are below
  },
  address: {
    width: '100%',
    flexDirection: 'row',
    alignItems: 'center',
  },
  icon: {
    marginRight: theme.spacing.medium,
    width: 24,
    height: 24,
  },
  placeholder: {
    marginTop: theme.spacing.medium,
  },
  placeholderSmall: {
    marginTop: theme.spacing.small,
  },
  text: {
    textAlign: 'center',
    marginHorizontal: theme.spacing.large,
  },
  mobileText: {
    width: '100%',
    paddingHorizontal: theme.spacing.medium,
  },
  hint: {
    color: theme.colors.CHARCOAL,
    marginBottom: theme.spacing.small,
  },
  addressMargin: {
    marginBottom: theme.spacing.large,
  },
  spacer: {
    marginBottom: theme.spacing.xlarge - theme.spacing.small,
  },
  smallSpacer: {
    marginBottom: theme.spacing.medium,
  },
  smallerSpacer: {
    marginBottom: theme.spacing.small,
  },
  illustrationWrapper: {
    paddingBottom: theme.spacing.large,
  },
  illustration: {
    width: 200,
    height: 200,
  },
  addressesDesktop: {
    marginTop: theme.spacing.large,
    paddingHorizontal: theme.spacing.medium,
  },
  addressesMobile: {
    position: 'absolute',
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    zIndex: theme.zIndex.sticky,
    paddingTop: theme.spacing.large,
    paddingHorizontal: theme.spacing.medium,
    backgroundColor: theme.colors.OATMEAL,
  },
});
